import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Clock, Eye, MinusCircle, XCircle } from 'lucide-react';
import type { CursorPage, Partner, PartnerStatus } from '@dru/types';
import { PARTNER_TYPE_LABEL } from '@dru/types';
import {
  Button, DataTable, FilterBar, IconAction, RowActions, SearchInput, StatusBadge, cn, type Tone,
} from '@dru/ui';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { useAuth } from '../../../lib/auth';
import { PageHeader, holds } from '../../../layouts/ConsoleShell';
import { PartnerRegisterDialog } from './PartnerRegister';

/**
 * The lab network: every hospital, diagnostic centre and laboratory the
 * platform can send a worker to, and the ones still waiting to be let in.
 *
 * Read as a comparison, not a registry: who it is and what kind, where it
 * is, whether it is empanelled, what it can run and how many a day — and,
 * in one plain line, whether it can receive a case today and if not why.
 * That last column is the one the list was missing; a partner that is
 * "Empanelled" with no tests declared looked exactly as ready as one that
 * could take a worker tomorrow. A row opens the partner. Filters live in
 * the URL so the dashboard can link at "the ones awaiting review".
 */
export function Partners() {
  const nav = useNavigate();
  const { permissions } = useAuth();
  // Finance reads this list for the commercial side and cannot register a
  // partner — the API refuses it — so the button is not offered to them.
  const canRegister = holds(permissions, 'partner.create');
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const type = params.get('type') ?? '';
  const setFilters = (next: Record<string, string | undefined>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) { if (v) p.set(k, v); else p.delete(k); }
    setParams(p, { replace: true });
  };
  const [search, setSearch] = useState('');

  const [term, setTerm] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setTerm(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const partners = useQuery<CursorPage<Partner & { status: PartnerStatus }>>(
    () => api.get(
      `/partners?limit=200&status=${status || 'ALL'}`
      + (type ? `&type=${type}` : '')
      + (term ? `&q=${encodeURIComponent(term)}` : ''),
    ),
    [status, type, term],
  );

  const rows = partners.data?.items ?? [];
  const filtered = Boolean(status || type || term);
  // In the URL, so the old /admin/partners/register link and a refresh both
  // come back to the open form.
  const registering = canRegister && params.get('register') === '1';
  const openRegister = () => setFilters({ register: '1' });

  return (
    <div className="space-y-4">
      <PageHeader
        className="mb-0"
        title="Lab network"
        hint="Hospitals and laboratories employees can be sent to, whether each can receive a case today, and the ones awaiting empanelment."
        actions={canRegister && (
          <Button onClick={openRegister}>Register a network partner</Button>
        )}
      />

      <FilterBar
        filters={[
          {
            key: 'type', label: 'Type',
            options: [
              { value: 'HOSPITAL', label: 'Hospitals' },
              { value: 'LABORATORY', label: 'Laboratories' },
              { value: 'BOTH', label: 'Hospital and laboratory' },
            ],
          },
          {
            key: 'status', label: 'Status',
            options: [
              { value: 'ONBOARDING', label: 'Awaiting review' },
              { value: 'ACTIVE', label: 'Empanelled' },
              { value: 'SUSPENDED', label: 'Suspended' },
              { value: 'REJECTED', label: 'Declined' },
              { value: 'TERMINATED', label: 'Terminated' },
            ],
          },
        ]}
        value={{ type: type || undefined, status: status || undefined }}
        onChange={setFilters}
      >
        <SearchInput
          value={search}
          onValueChange={setSearch}
          placeholder="Search by name, code or town"
          aria-label="Search the lab network"
          className="w-full sm:w-80"
        />
      </FilterBar>

      <DataTable
        rows={rows}
        loading={partners.loading}
        error={partners.error}
        onRetry={partners.reload}
        rowKey={(p) => p.id}
        onRowClick={(p) => nav(`/admin/partners/${p.id}`)}
        empty={filtered
          ? { title: term ? `Nothing matches “${term}”` : 'No partners match these filters', hint: 'Try the name, its code or the town — or clear the filters.' }
          : {
              title: 'No partners in the network yet',
              hint: 'Register a hospital or laboratory; it can receive cases once its tests are declared and it is empanelled.',
              action: canRegister ? <Button onClick={openRegister}>Register a network partner</Button> : undefined,
            }}
        columns={[
          {
            key: 'name', header: 'Lab or hospital', alwaysVisible: true,
            cell: (p) => (
              <div className="min-w-0">
                <div className="truncate font-medium text-neutral-900">{p.name}</div>
                <div className="truncate text-caption font-normal text-neutral-500">
                  {p.networkType?.name ?? (PARTNER_TYPE_LABEL[p.type] ?? p.type)}
                  {' · '}<span className="font-mono">{p.code}</span>
                </div>
              </div>
            ),
          },
          {
            key: 'where', header: 'Location',
            cell: (p) => (
              <div className="min-w-0 text-caption font-normal">
                {p.city || p.locality ? (
                  <>
                    <div className="truncate text-neutral-800">{[p.locality, p.city].filter(Boolean).join(', ')}</div>
                    <div className="truncate text-neutral-500">{[p.state, p.pincode].filter(Boolean).join(' ')}</div>
                  </>
                ) : <span className="text-neutral-500">Address not recorded</span>}
              </div>
            ),
          },
          {
            // On a card the badge sits beside the heading, not on a line of its own.
            key: 'status', header: 'Status', mobileLabel: '',
            cell: (p) => <StatusBadge tone={partnerTone(p.status)}>{STATUS_SHORT[p.status] ?? p.status}</StatusBadge>,
          },
          {
            key: 'capacity', header: 'Tests and capacity', mobileLabel: 'Runs',
            cell: (p) => (
              <div className="text-caption font-normal tabular-nums">
                <div className={p.testsOffered ? 'text-neutral-800' : 'text-neutral-500'}>
                  {p.testsOffered ? `${p.testsOffered} test${p.testsOffered === 1 ? '' : 's'}` : 'No tests declared'}
                </div>
                <div className="text-neutral-500">{p.dailyCapacity} employees a day</div>
              </div>
            ),
          },
          {
            key: 'ready', header: 'Can receive cases?', mobileLabel: 'Readiness',
            cell: (p) => <Readiness {...readinessOf(p)} />,
          },
          {
            key: 'actions', header: 'Actions', align: 'right', alwaysVisible: true, mobileLabel: '',
            cell: (p) => (
              <RowActions>
                <IconAction label={`View ${p.name}`} icon={Eye} onClick={() => nav(`/admin/partners/${p.id}`)} />
              </RowActions>
            ),
          },
        ]}
      />

      {registering && <PartnerRegisterDialog onClose={() => setFilters({ register: undefined })} />}
    </div>
  );
}

/**
 * The badge in a list, where the readiness column beside it already says
 * "not routable" — so the long form the detail header uses is not repeated.
 */
const STATUS_SHORT: Record<PartnerStatus, string> = {
  ONBOARDING: 'Awaiting review',
  ACTIVE: 'Empanelled',
  SUSPENDED: 'Suspended',
  REJECTED: 'Declined',
  TERMINATED: 'Terminated',
};

type ReadyTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

/**
 * Whether a partner can take a case, from what the list already carries.
 *
 * Deliberately coarse: the detail page holds the full readiness list
 * (contact, staff, rate card). Here it is the first thing that stops it,
 * so a column of these can be scanned for the odd one out.
 */
function readinessOf(p: Partner & { status: PartnerStatus }): { tone: ReadyTone; text: string } {
  switch (p.status) {
    case 'REJECTED': return { tone: 'neutral', text: 'Application declined' };
    case 'TERMINATED': return { tone: 'neutral', text: 'Relationship ended' };
    case 'SUSPENDED': return { tone: 'warning', text: 'Paused — not receiving cases' };
    case 'ONBOARDING':
      return p.testsOffered
        ? { tone: 'info', text: 'Ready for empanelment review' }
        : { tone: 'warning', text: 'Declare tests before approval' };
    default:
      if (!p.testsOffered) return { tone: 'danger', text: 'No tests declared — cannot be routed' };
      if (!p.city && !p.locality) return { tone: 'warning', text: 'No address to send employees to' };
      if (p.dailyCapacity <= 0) return { tone: 'warning', text: 'No daily capacity set' };
      return { tone: 'success', text: 'Receiving cases' };
  }
}

const READY_STYLE: Record<ReadyTone, { text: string; Icon: typeof CheckCircle2 }> = {
  success: { text: 'text-success-600', Icon: CheckCircle2 },
  warning: { text: 'text-warning-600', Icon: AlertTriangle },
  danger: { text: 'text-danger-600', Icon: XCircle },
  info: { text: 'text-info-600', Icon: Clock },
  neutral: { text: 'text-neutral-600', Icon: MinusCircle },
};

/** Icon and words together — the colour is never the only thing saying it. */
function Readiness({ tone, text }: { tone: ReadyTone; text: string }) {
  const { text: cls, Icon } = READY_STYLE[tone];
  return (
    <span className={cn('inline-flex items-start gap-1.5 text-caption font-medium', cls)}>
      <Icon aria-hidden className="mt-px h-3.5 w-3.5 shrink-0" />
      {text}
    </span>
  );
}

function partnerTone(s: PartnerStatus): Tone {
  switch (s) {
    case 'ACTIVE': return 'covered';
    case 'ONBOARDING': return 'dueSoon';
    case 'SUSPENDED': return 'overdue';
    case 'REJECTED': return 'neutral';
    default: return 'neutral';
  }
}
